import { DepGraph } from 'dependency-graph';
import { getCategoriesFromResource, getSpotlightConfigurationForResource, } from '../compiler/lightdashProjectConfig';
import assertUnreachable from '../utils/assertUnreachable';
import { getItemId } from '../utils/item';
import { DbtError, ParseError } from './errors';
import { FieldType, friendlyName, } from './field';
import { parseFilters } from './filterGrammar';
export var SupportedDbtAdapter;
(function (SupportedDbtAdapter) {
    SupportedDbtAdapter["BIGQUERY"] = "bigquery";
    SupportedDbtAdapter["DATABRICKS"] = "databricks";
    SupportedDbtAdapter["SNOWFLAKE"] = "snowflake";
    SupportedDbtAdapter["REDSHIFT"] = "redshift";
    SupportedDbtAdapter["POSTGRES"] = "postgres";
    SupportedDbtAdapter["TRINO"] = "trino";
})(SupportedDbtAdapter || (SupportedDbtAdapter = {}));
export const normaliseModelDatabase = (model, targetWarehouse) => {
    switch (targetWarehouse) {
        case SupportedDbtAdapter.POSTGRES:
        case SupportedDbtAdapter.BIGQUERY:
        case SupportedDbtAdapter.SNOWFLAKE:
        case SupportedDbtAdapter.TRINO:
        case SupportedDbtAdapter.REDSHIFT:
            if (model.database === null) {
                throw new ParseError(`Cannot parse dbt model '${model.unique_id}' because the database field has null value.`, {});
            }
            return { ...model, database: model.database };
        case SupportedDbtAdapter.DATABRICKS:
            return { ...model, database: model.database || 'DEFAULT' };
        default:
            return assertUnreachable(targetWarehouse, `Cannot recognise warehouse ${targetWarehouse}`);
    }
};
export const patchPathParts = (patchPath) => {
    const [project, ...rest] = patchPath.split('://');
    if (rest.length === 0) {
        throw new DbtError('Could not parse dbt manifest. It looks like you might be using an old version of dbt. You must be using dbt version 0.20.0 or above.');
    }
    return {
        project,
        path: rest.join('://'),
    };
};
export const buildModelGraph = (allModels) => {
    const depGraph = new DepGraph();
    const lookup = Object.fromEntries(allModels.map((model) => [model.unique_id, model]));
    allModels.forEach((model) => {
        depGraph.addNode(model.unique_id, { type: 'model', name: model.name });
        // Only use models for graph.
        model.depends_on?.nodes?.forEach((nodeId) => {
            const node = lookup[nodeId];
            if (node) {
                depGraph.addNode(node.unique_id, {
                    type: 'model',
                    name: node.name,
                });
                depGraph.addDependency(model.unique_id, node.unique_id);
            }
        });
    });
    return depGraph;
};
export const isDbtRpcDocsGenerateResults = (results) => 'nodes' in results &&
    typeof results.nodes === 'object' &&
    results.nodes !== null &&
    Object.values(results.nodes).every((node) => typeof node === 'object' &&
        node !== null &&
        'metadata' in node &&
        'columns' in node);
export const isDbtPackages = (results) => 'packages' in results;
export const isV9MetricRef = (x) => typeof x === 'object' && x !== null && 'name' in x;
const isDbtRawManifestMetadata = (x) => typeof x === 'object' &&
    x !== null &&
    'dbt_schema_version' in x &&
    'generated_at' in x &&
    'adapter_type' in x;
export const isSupportedDbtAdapter = (x) => isDbtRawManifestMetadata(x) &&
    Object.values(SupportedDbtAdapter).includes(x.adapter_type);
export const isSupportedDbtAdapterType = (x) => Object.values(SupportedDbtAdapter).includes(x);
export const isDbtRpcManifestResults = (results) => 'manifest' in results &&
    typeof results.manifest === 'object' &&
    results.manifest !== null &&
    'nodes' in results.manifest &&
    'metadata' in results.manifest &&
    'metrics' in results.manifest &&
    isDbtRawManifestMetadata(results.manifest.metadata);
export const isDbtRpcCompileResults = (results) => 'results' in results &&
    Array.isArray(results.results) &&
    results.results.every((result) => typeof result === 'object' &&
        result !== null &&
        'node' in result &&
        typeof result.node === 'object' &&
        result.node !== null &&
        'unique_id' in result.node &&
        'resource_type' in result.node);
export const convertToGroups = (dbtGroups, dbtGroupLabel) => {
    let groups = [];
    if (dbtGroups) {
        if (typeof dbtGroups === 'string') {
            groups = [dbtGroups];
        }
        else {
            groups = [...dbtGroups];
        }
    }
    else if (dbtGroupLabel) {
        groups = [dbtGroupLabel];
    }
    return groups;
};
export const isDbtRpcRunSqlResults = (
// eslint-disable-next-line @typescript-eslint/no-explicit-any
results) => 'results' in results &&
    Array.isArray(results.results) &&
    results.results.every((result) => typeof result === 'object' &&
        result !== null &&
        'table' in result &&
        typeof result.table === 'object' &&
        result.table !== null &&
        'column_names' in result.table &&
        Array.isArray(result.table.column_names) &&
        'rows' in result.table &&
        Array.isArray(result.table.rows));
export const convertModelMetric = ({ modelName, name, metric, source, tableLabel, dimensionReference, requiredAttributes, spotlightConfig, modelCategories = [], }) => {
    const groups = convertToGroups(metric.groups, metric.group_label);
    const spotlightVisibility = metric.spotlight?.visibility ?? spotlightConfig?.default_visibility;
    const metricCategories = Array.from(new Set([...modelCategories, ...(metric.spotlight?.categories || [])]));
    const spotlightCategories = getCategoriesFromResource('metric', name, spotlightConfig, metricCategories);
    return {
        fieldType: FieldType.METRIC,
        name,
        label: metric.label || friendlyName(name),
        sql: metric.sql,
        table: modelName,
        tableLabel,
        type: metric.type,
        description: metric.description,
        source,
        hidden: !!metric.hidden,
        round: metric.round,
        compact: metric.compact,
        format: metric.format,
        groups,
        showUnderlyingValues: metric.show_underlying_values,
        filters: parseFilters(metric.filters),
        percentile: metric.percentile,
        dimensionReference,
        requiredAttributes,
        ...(metric.urls ? { urls: metric.urls } : null),
        ...(metric.tags
            ? {
                tags: Array.isArray(metric.tags)
                    ? metric.tags
                    : [metric.tags],
            }
            : null),
        ...(metric.default_time_dimension
            ? {
                defaultTimeDimension: {
                    field: metric.default_time_dimension.field,
                    interval: metric.default_time_dimension.interval,
                },
            }
            : null),
        ...getSpotlightConfigurationForResource(spotlightVisibility, spotlightCategories),
    };
};
export const convertColumnMetric = ({ modelName, dimensionName, dimensionSql, name, metric, source, tableLabel, requiredAttributes, spotlightConfig, modelCategories = [], }) => convertModelMetric({
    modelName,
    name,
    metric: {
        ...metric,
        sql: metric.sql || dimensionSql,
        description: metric.description ||
            (dimensionName
                ? `${friendlyName(metric.type)} of ${friendlyName(dimensionName)}`
                : undefined),
    },
    source,
    tableLabel,
    dimensionReference: dimensionName
        ? getItemId({ table: modelName, name: dimensionName })
        : undefined,
    requiredAttributes,
    ...(metric.default_time_dimension
        ? {
            defaultTimeDimension: {
                field: metric.default_time_dimension.field,
                interval: metric.default_time_dimension.interval,
            },
        }
        : null),
    spotlightConfig,
    modelCategories,
});
export var DbtManifestVersion;
(function (DbtManifestVersion) {
    DbtManifestVersion["V7"] = "v7";
    DbtManifestVersion["V8"] = "v8";
    DbtManifestVersion["V9"] = "v9";
    DbtManifestVersion["V10"] = "v10";
    DbtManifestVersion["V11"] = "v11";
    DbtManifestVersion["V12"] = "v12";
})(DbtManifestVersion || (DbtManifestVersion = {}));
export const getDbtManifestVersion = (manifest) => {
    const version = manifest.metadata.dbt_schema_version.match(/\/(v\d+).json/)?.[1];
    if (!version) {
        throw new Error(`Could not determine dbt manifest version from ${manifest.metadata.dbt_schema_version}`);
    }
    if (Object.values(DbtManifestVersion).includes(version)) {
        return version;
    }
    throw new Error(`Unsupported dbt manifest version: ${version}`);
};
export const getLatestSupportedDbtManifestVersion = () => {
    const versions = Object.values(DbtManifestVersion);
    return versions[versions.length - 1];
};
export var DbtExposureType;
(function (DbtExposureType) {
    DbtExposureType["DASHBOARD"] = "dashboard";
    DbtExposureType["NOTEBOOK"] = "notebook";
    DbtExposureType["ANALYSIS"] = "analysis";
    DbtExposureType["ML"] = "ml";
    DbtExposureType["APPLICATION"] = "application";
})(DbtExposureType || (DbtExposureType = {}));
export const getModelsFromManifest = (manifest) => {
    const models = Object.values(manifest.nodes).filter((node) => node.resource_type === 'model' &&
        node.config?.materialized !== 'ephemeral');
    if (!isSupportedDbtAdapter(manifest.metadata)) {
        throw new ParseError(`dbt adapter not supported. Lightdash does not support adapter ${manifest.metadata.adapter_type}`, {});
    }
    const adapterType = manifest.metadata.adapter_type;
    return models
        .filter((model) => model.config?.materialized &&
        model.config.materialized !== 'ephemeral')
        .map((model) => normaliseModelDatabase(model, adapterType));
};
export function getCompiledModels(manifestModels, compiledModelIds) {
    return manifestModels.filter((model) => {
        if (compiledModelIds) {
            return compiledModelIds.includes(model.unique_id);
        }
        return model.compiled;
    });
}
//# sourceMappingURL=dbt.js.map